import { debounce } from "lodash";
import { useCallback } from "react";
import CreateCharacterMpProvider from "@/core/data-provider/data-mp/create-character/create-character-mp-provider.ts";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import {
  CUSTOMIZATION_FATHERS,
  CUSTOMIZATION_MOTHERS,
} from "@/domains/createCharacter/constants/general";
import { Identical } from "@/domains/createCharacter/constants/genetics";
import {
  CreateCharacterState,
  currentIdenticalVariantSelector,
  geneticsSelector,
  identicalVariantsSelector,
  setGenetics,
  setIdenticalVariant,
} from "@/domains/createCharacter/slice";
import randomArrayIndex from "@/domains/createCharacter/helpers/randomArrayIndex";

export default function useChangeGenetics() {
  const dispatch = useAppDispatch();
  const identicalVariants = useAppSelector(identicalVariantsSelector);
  const currentIdenticalVariant = useAppSelector(currentIdenticalVariantSelector);
  const genetics = useAppSelector(geneticsSelector);

  const onChangeIdenticalView = useCallback(
    async (identical: Identical, value: number) => {
      const [father, mother] = identicalVariants[currentIdenticalVariant];

      // const isHetero = father === Identical.Father && mother === Identical.Mother;

      if (father === Identical.Father && mother === Identical.Mother) {
        if (identical === Identical.Father) {
          dispatch(setGenetics({ father: value }));
          await CreateCharacterMpProvider.setGenetics({
            ...genetics,
            father: value,
          });
        }

        if (identical === Identical.Mother) {
          dispatch(setGenetics({ mother: value }));
          await CreateCharacterMpProvider.setGenetics({
            ...genetics,
            mother: value,
          });
        }
      }

      if (father === Identical.Father && mother === Identical.GrandFather) {
        if (identical === Identical.Father) {
          dispatch(setGenetics({ father: value }));
          await CreateCharacterMpProvider.setGenetics({
            ...genetics,
            father: value,
          });
        }

        if (identical === Identical.GrandFather) {
          dispatch(setGenetics({ mother: value }));
          await CreateCharacterMpProvider.setGenetics({
            ...genetics,
            mother: value,
          });
        }
      }

      if (father === Identical.Mother && mother === Identical.GrandMother) {
        if (identical === Identical.Mother) {
          dispatch(setGenetics({ father: value }));
          await CreateCharacterMpProvider.setGenetics({
            ...genetics,
            father: value,
          });
        }

        if (identical === Identical.GrandMother) {
          dispatch(setGenetics({ mother: value }));
          await CreateCharacterMpProvider.setGenetics({
            ...genetics,
            mother: value,
          });
        }
      }
    },
    [identicalVariants, currentIdenticalVariant, dispatch, genetics],
  );

  const onChangeSimilarity = useCallback(
    async (value: number) => {
      dispatch(setGenetics({ similarity: value }));
      await CreateCharacterMpProvider.setGenetics({
        ...genetics,
        similarity: value,
      });
    },
    [genetics, dispatch],
  );

  const onChangeSkin = useCallback(
    async (value: number) => {
      dispatch(setGenetics({ skin: value }));
      await CreateCharacterMpProvider.setGenetics({
        ...genetics,
        skin: value,
      });
    },
    [genetics, dispatch],
  );

  const randomizeAll = useCallback(
    debounce(async () => {
      const randomView: CreateCharacterState["genetics"] = {
        father: CUSTOMIZATION_FATHERS[randomArrayIndex(CUSTOMIZATION_FATHERS.length)],
        mother: CUSTOMIZATION_MOTHERS[randomArrayIndex(CUSTOMIZATION_MOTHERS.length)],
        similarity: Math.random(),
        skin: Math.random(),
      };

      dispatch(setIdenticalVariant(randomArrayIndex(identicalVariants.length)));

      dispatch(setGenetics(randomView));
      await CreateCharacterMpProvider.setGenetics(randomView);
    }, 50),
    [dispatch, identicalVariants],
  );

  return {
    randomizeAll,
    onChangeIdenticalView,
    onChangeSimilarity,
    onChangeSkin,
  };
}
